/**
 * The property types a guest can browse by, and the only values the
 * /stays/{type} pages will answer for.
 *
 * A closed list on purpose: this segment shares its position with the old
 * city segment, so "riad" has to be distinguishable from "marrakech" to know
 * which page was asked for. Adding a type here is what makes its page exist.
 *
 * "villa" joined the list on 2026-08-18. It was deliberately absent while we
 * sold partner villas from the activities catalogue: /category/villa had to
 * stay an activities hub or those villas would have vanished off it. That
 * partnership ended, every one of those rows is unpublished, and the only
 * villas we sell now are supplier properties — so the hub is theirs.
 *
 * "guesthouse" joined on 2026-09-06, for the opposite reason: six published
 * guesthouses were stored under a type with no hub, so they existed on their
 * own pages and in the city lists and appeared in no chip row at all. A type
 * the database accepts but this list omits is a property nobody can browse to.
 */
export const STAY_PROPERTY_TYPES = ["hotel", "riad", "resort", "villa", "guesthouse"] as const;
export type StayPropertyType = (typeof STAY_PROPERTY_TYPES)[number];

export function isStayPropertyType(value: string): value is StayPropertyType {
  return (STAY_PROPERTY_TYPES as readonly string[]).includes(value);
}

import { z } from "zod";
import { MAX_STAY_CHILDREN, MAX_STAY_CHILD_AGE } from "./stays";
import { SUPPORTED_LANGS } from "../i18n/langs";
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from "../constants/currencies";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

export const stayPropertyAmenitySchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
});

/**
 * One question and its answer, in the shape the activity FAQ component and the
 * FAQPage structured data already expect, so a stay reuses both unchanged.
 */
export const stayPropertyFaqSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
});

/**
 * The curated-catalogue property DTO served to clients.
 *
 * `.strip()` here is load-bearing, not decorative: `supplierHotelId` is our
 * only link to the LiteAPI hotel and must never reach a client — exposing it
 * would let anyone price our inventory directly against the supplier. Using
 * `.strip()` (Zod's default, made explicit) means any field not declared
 * below — including `supplierHotelId` if it's ever spread onto the row before
 * serialising — is dropped by construction, so a later widened query or a
 * careless `{ ...row }` cannot leak it.
 */
export const stayPropertySchema = z
  .object({
    id: z.string(),
    slug: z.string().min(1),
    citySlug: z.string().min(1),
    name: z.string().min(1),
    summary: z.string().min(1),
    description: z.string().min(1),
    neighbourhoodNotes: z.string().nullable(),
    /** Our taxonomy: riad, hotel, villa, ... Drives /category/<type>/<city>. */
    propertyType: z.string().min(1),
    starRating: z.number().int().min(1).max(5).nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    address: z.string().nullable(),
    amenities: z.array(stayPropertyAmenitySchema),
    /**
     * Questions a guest asks before booking. Empty when we have nothing worth
     * answering, so the page shows no FAQ section rather than a padded one.
     */
    faqs: z.array(stayPropertyFaqSchema).default([]),
    /**
     * Entry conditions the property enforces at the door, as keys the client
     * translates. Never the supplier's prose: it ships in English and French
     * only, and much of it describes Booking's pay-at-property channel rather
     * than ours.
     */
    houseRules: z.array(z.string()).default([]),
    images: z.array(z.string()),
    relatedActivityIds: z.array(z.string()),
    /**
     * Indicative cheapest nightly rate, margin included, or null when we have
     * none. Lets an undated card show "from MAD 1,200 a night" instead of
     * nothing, since the supplier will not quote without dates.
     *
     * NOT an offer for any particular date: it is the cheapest rate found in
     * one sample window by the daily refresh job (stays.from-price.ts), which
     * is why every surface that renders it must say "from". A live quote for
     * the guest's own dates always supersedes it.
     */
    fromPriceNightly: z.number().positive().nullable().default(null),
    fromPriceCurrency: z.string().nullable().default(null),
    /**
     * Check-in dates the nightly sweep found nothing bookable on. The only
     * dates a calendar may disable.
     *
     * A SNAPSHOT, never a guarantee. Inventory moves during the day and the
     * sweep probes one fixed stay length for one sample party, so a date left
     * selectable can still fail at search — the live check on this page is
     * unchanged and still decides. What this ends is guests hunting blind: on
     * sparse villa inventory nearly every date a guest tried came back "no
     * rooms", and from their side that is indistinguishable from a broken page.
     *
     * Empty is not "everything is free" — read it with `availabilityCheckedThrough`.
     */
    /**
     * The largest party this property will take, from the supplier's
     * `maxOccupancy`. Null when unknown.
     *
     * Villa Jannah's page says it sleeps eight; the supplier sells it to a
     * maximum of four, and a party of five was told "No rooms for these dates.
     * Try different dates." The dates were fine. This is what lets the widget
     * cap the guest stepper and say what the real limit is instead of sending
     * someone to re-pick dates that already worked.
     */
    maxGuests: z.number().int().positive().nullable().default(null),
    /**
     * Check-in and check-out times as the property states them, e.g.
     * "02:30 PM". Strings, not times: the supplier gives no timezone, so
     * repeating the words is the only honest option. Null when unstated.
     */
    checkInFrom: z.string().nullable().default(null),
    checkInUntil: z.string().nullable().default(null),
    checkOutBy: z.string().nullable().default(null),
    unavailableDates: z.array(isoDate).default([]),
    /**
     * The furthest check-in date the sweep has an answer for, or null when it
     * has none for this property (never swept, or the answers went stale).
     *
     * Beyond it, and whenever it is null, availability is UNKNOWN and every
     * date must stay selectable. Suppliers routinely have no rates loaded
     * months out, so treating "no answer" as "sold out" would invent
     * unavailable inventory across the whole of next year.
     *
     * Deliberately null on list responses: only the property detail response
     * populates the map, because a city page has no calendar to disable and
     * would otherwise carry up to ninety date strings per card.
     */
    availabilityCheckedThrough: isoDate.nullable().default(null),
    /**
     * THERE IS NO minNights HERE, AND NOTHING MAY ADD ONE BACK THAT IS NOT
     * THE SUPPLIER'S OWN NUMBER.
     *
     * A `minNights` was published from this schema. It came from the nightly
     * sweep, which derived it by trying stay lengths until one sold and
     * keeping the shortest that did. Nothing about it came from the supplier:
     * LiteAPI's rates response carries no minimumStay, lengthOfStay or
     * closedToArrival field, and no endpoint of theirs returns stay
     * restrictions. A range that breaks a property's rules simply comes back
     * empty, with no reason attached, so a minimum stay cannot be read — only
     * guessed at.
     *
     * The guess was published as though the property had said it, and the
     * calendar enforced it. Measured on Riad KS & Spa 2026-08-22: the derived
     * floor was two nights, so we refused the one-night stay Booking.com was
     * selling on 31 August at 2,901 MAD, and every property in the catalogue
     * carried a floor of at least two because two was the shortest length the
     * sweep ever asked about.
     *
     * `unavailableDates` stays, and is a different kind of claim: "the
     * supplier returned nothing at any length we tried on this date" is an
     * answer it actually gave. "The minimum stay is N" is not.
     *
     * `shortestStaySeenNights` below is a third kind again. Read its note.
     */
    /**
     * The shortest stay this property has been OBSERVED selling, in nights,
     * and only when the observation is strong enough to be worth repeating.
     * Null for most properties, and never below 2.
     *
     * NOT A MINIMUM STAY, AND NEVER TO BE ENFORCED. It exists to put a reason
     * under an empty search result and nothing else. Whatever the number, the
     * calendar must still accept the guest's click and the live search must
     * still be the thing that decides: 79 of 91 properties turned out to sell
     * single nights, and refusing those clicks is precisely what cost us the
     * one-night booking Booking.com took on 31 August.
     *
     * WHY THIS IS SOUND WHERE THE OLD minNights WAS NOT. That one came from a
     * single probe, and a single refusal from this supplier is wrong about
     * 60% of the time (see the retry note in liteapi.client.ts). This is the
     * shortest length seen across EVERY swept date, and it is only published
     * once a property has sold on at least
     * `SHORTEST_STAY_EVIDENCE_DATES` separate dates without ever selling
     * shorter. Riad Dar Tiflet sold on 75 dates, none of them a single night:
     * one false refusal is likely, seventy-five in a row is not. A ceiling
     * could never be established this way, because a ceiling rests on the
     * last refusal alone — which is exactly why `maxGuests` is read from
     * published content instead.
     */
    shortestStaySeenNights: z.number().int().min(2).nullable().default(null),
  })
  .strip();

export type StayProperty = z.infer<typeof stayPropertySchema>;
export type StayPropertyAmenity = z.infer<typeof stayPropertyAmenitySchema>;

/**
 * Nationality used when the guest's own is unknown.
 *
 * Morocco, because it is what every stay search sent before the visitor's
 * country was forwarded at all — so an unknown country degrades to the exact
 * behaviour that shipped for a year, never to a market nobody chose.
 */
export const DEFAULT_GUEST_NATIONALITY = "MA";

export const stayPropertyListQuerySchema = z
  .object({
    lang: z.enum(SUPPORTED_LANGS).default("en"),
    /** Narrow a city list to one stay type, e.g. "riad". Powers /category/<type>/<city>. */
    propertyType: z.string().min(1).optional(),
    /**
     * The currency the guest picked in the header, forwarded to the supplier
     * so the quote comes back already denominated in it.
     *
     * Deliberately a real supplier quote rather than a conversion we apply on
     * the way out: a stay is charged online in full, so the number on the
     * booking widget is the number that hits the card. Converting a MAD quote
     * ourselves would show a USD figure the payment never matches.
     *
     * An unknown code falls back to MAD rather than erroring — a stale
     * bookmark carrying `currency=CHF` should show a page, not a 400.
     */
    currency: z
      .enum(SUPPORTED_CURRENCIES)
      .catch(DEFAULT_CURRENCY)
      .default(DEFAULT_CURRENCY),
    /**
     * The guest's nationality (ISO-3166 alpha-2), forwarded to the supplier.
     *
     * NOT cosmetic. LiteAPI prices and gates inventory on it: a supplier
     * contract can be sold to some markets and not others, so a search run
     * under the wrong nationality can quote a rate the guest is then refused
     * at prebook. Every search sent a hard-coded "MA" until 2026-09-22, so a
     * French guest was shopping the Moroccan-resident market.
     *
     * Best effort by design. We never ask for a passport, so this is the
     * visitor's country from Cloudflare's edge signal, which is where they
     * are rather than who they are. Wrong for an expat and unknown behind a
     * VPN — both fall back to MA, which is exactly the old behaviour.
     *
     * Junk falls back rather than erroring, like `currency` above: a stale
     * bookmark carrying `guestNationality=ZZZ` should show a page, not a 400.
     */
    guestNationality: z
      .string()
      .transform((v) => v.trim().toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{2}$/))
      .catch(DEFAULT_GUEST_NATIONALITY)
      .default(DEFAULT_GUEST_NATIONALITY),
    /**
     * Opaque per-browser-session id, forwarded to the supplier so a guest's
     * quoted price stays put between searching and checking out. Never
     * inspected, never stored, never used to identify anyone.
     */
    sessionId: z.string().min(1).max(128).optional(),
    checkin: isoDate.optional(),
    checkout: isoDate.optional(),
    adults: z.coerce.number().int().min(1).max(10).optional(),
    /**
     * Comma separated ages, one per child, e.g. "childAges=2,15". A count
     * would not do: the supplier prices on the age itself, and the gap is
     * large enough to mis-sell a room (see staySearchQuerySchema).
     */
    childAges: z
      .union([z.string(), z.array(z.coerce.number())])
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        const raw = Array.isArray(v) ? v : v.split(",");
        const ages = raw
          .map((n) => Number(String(n).trim()))
          .filter((n) => Number.isInteger(n) && n >= 0 && n <= MAX_STAY_CHILD_AGE);
        return ages.slice(0, MAX_STAY_CHILDREN);
      }),
  })
  .refine((v) => !v.checkin || !v.checkout || v.checkout > v.checkin, {
    message: "checkout must be after checkin",
    path: ["checkout"],
  })
  .refine(
    (v) => {
      const provided = [v.checkin, v.checkout, v.adults].filter((x) => x !== undefined).length;
      return provided === 0 || provided === 3;
    },
    {
      message: "checkin, checkout and adults must all be supplied together for live pricing",
      path: ["adults"],
    },
  );

export type StayPropertyListQuery = z.infer<typeof stayPropertyListQuerySchema>;
